import React, { useEffect, useRef, useState } from "react";
import AddNewPassword from "./NewPassword";
import { useShowAlert } from "../../../../context/AlertContext";
import AxiosInstance from "../../../../utils/AxiosInstance";
import handleError from "../../../../constants/handleError";
import { useNetwork } from "../../../../context/NetworkContext";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress, Skeleton } from "@mui/material";
import DeleteModal from "../../../../constants/DeleteModal";
import EditPassword from "./Editpassword";
import { useAuth } from "../../../../context/AuthProvider";
import { AddIcon, CopyIcon, DeleteIcon, EditIcon, LockAltIcon, NoteIcon, SearchIcon, UserIcon } from "../../../../constants/Icons";

const PasswordManager = ({ customerId }) => {
  const { isUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordUnlocked, setPasswordUnlocked] = useState(false);
  const [isListPassword, setListPassword] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sidebarRef = useRef(null);
  const isLoadingMoreRef = useRef(isLoadingMore);
  const hasMoreRef = useRef(hasMore);
  const showAlert = useShowAlert();
  const { checkNetwork } = useNetwork();
  const perPage = 10;

  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const handleAddPassword = async (data) => {
    try {
      const datas = {
        customerid: customerId,
        title: data.domain,
        userName: data.username,
        password: data.password,
        notes: data.notes,
      };
      const response = await AxiosInstance.post("module/password", datas);
      const result = response.data.results;
      showAlert("success", "Password added successfully");

      // update local state instantly
      setListPassword((prev) => [result, ...prev]);

      setShowModal(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleListPassword = async (page = 0) => {
    try {
      if (!checkNetwork()) return;
      page === 0 ? setIsLoading(true) : setIsLoadingMore(true);

      const response = await AxiosInstance.get(
        `module/password/customer/${customerId}?pageno=${
          page + 1
        }&limit=${perPage}`
      );
      const results = response.data.results || [];

      if (page === 0) {
        setListPassword(results);
      } else {
        setListPassword((prev) => [...prev, ...results]);
      }
      setHasMore(results.length === perPage);
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleViewPassword = async (passwordObj) => {
    try {
      setIsPasswordLoading(true);
      const data = passwordObj._id;
      const response = await AxiosInstance.get(`module/password/${data}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const onUpdatePassword = async ({ password }) => {
    try {
      await AxiosInstance.patch(`module/password/${selected._id}`, {
        password,
      });
      showAlert("success", "Password updated successfully");
      setListPassword((prev) =>
        prev.map((p) => (p._id === selected._id ? { ...p, password } : p))
      );
      setSelected((prev) =>
        prev ? { ...prev, password: null, passwordUpdated: Date.now() } : prev
      );
      setPasswordUnlocked(false);
      setShowPassword(false);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const onUpdateDetails = async ({ domain, username, notes }) => {
    try {
      const response = await AxiosInstance.patch(
        `module/password/details/${selected._id}`,
        {
          title: domain,
          userName: username,
          notes,
        }
      );
      showAlert("success", "Details updated successfully");
      setListPassword((prev) =>
        prev.map((p) =>
          p._id === selected._id
            ? { ...p, title: domain, userName: username, notes }
            : p
        )
      );
      setSelected((prev) => ({
        ...prev,
        title: domain,
        userName: username,
        notes,
      }));
      setShowEditModal(false);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const openDeleteModal = (cItem) => {
    setDeleteData(cItem);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!checkNetwork()) return;
      await AxiosInstance.delete(`module/password/${isDeleteData._id}`);
      showAlert("success", "Password deleted successfully");

      setListPassword((prev) => {
        const index = prev.findIndex((p) => p._id === isDeleteData._id);
        if (index === -1) return prev;
        const newList = prev.filter((p) => p._id !== isDeleteData._id);
        if (newList.length > 0) {
          if (index < newList.length) {
            setSelected(newList[index]);
          } else {
            setSelected(newList[index - 1]);
          }
        } else {
          setSelected(null);
        }
        return newList;
      });
      setDeleteModalOpen(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await AxiosInstance.get(`module/password/details/${id}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleCopy = (value, field) => {
    navigator.clipboard.writeText(value).then(() => {
      showAlert("success", `Copied ${field}!`);
    });
  };

  useEffect(() => {
    if (!selected?._id) return;

    const fetchDetails = async () => {
      const details = await handleViewDetails(selected._id);
      if (details) {
        setSelected((prev) => ({
          ...prev,
          title: details.title,
          userName: details.userName,
          notes: details.notes,
        }));
      }
    };

    fetchDetails();
  }, [selected?._id]);

  useEffect(() => {
    if (!selected) return;
    setPasswordUnlocked(false);
    setShowPassword(false);
    setSelected((prev) => (prev ? { ...prev, password: null } : prev));
  }, [selected?._id, selected?.passwordUpdated]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
    hasMoreRef.current = hasMore;
  }, [isLoadingMore, hasMore]);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      if (
        scrollHeight - scrollTop - clientHeight < 50 &&
        !isLoadingMoreRef.current &&
        hasMoreRef.current
      ) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    sidebar.addEventListener("scroll", handleScroll);
    return () => sidebar.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    handleListPassword(currentPage);
  }, [currentPage]);

  return (
    <div className="">
      <div className="app-chat card overflow-hidden">
        <div className="row g-0">
          {/* ---------------- LEFT SIDEBAR ---------------- */}
          <div
            className={`col app-chat-contacts app-sidebar flex-grow-0 overflow-hidden border-end bg-white`}
            id="app-chat-contacts"
          >
            <div
              className="sidebar-header h-px-75 border-bottom d-flex align-items-center px-3 justify-content-between"
              style={{ width: "auto" }}
            >
              {/* Left section - search input */}
              <div
                className="input-group me-3 flex-grow-1"
                style={{ maxWidth: "70%" }}
              >
                <button className="input-group-text">
                  <SearchIcon/>
                </button>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                />
              </div>

              {/* Right section - add + close buttons */}
              <div className="d-flex align-items-center justify-content-end">
                {" "}
                <button className="btn btn-primary me-2" onClick={handleOpen}>
                  <AddIcon/>
                </button>
                {/* Close button (visible only on mobile) */}
                <button
                  type="button"
                  className="btn-close shadow-none fs-12  d-lg-none"
                  onClick={() => {
                    const sidebar =
                      document.getElementById("app-chat-contacts");
                    const overlay = document.getElementById("sidebar-overlay");
                    sidebar?.classList.remove("show");
                    overlay?.remove();
                  }}
                >
                  {/* <i className="bx bx-x"></i> */}
                </button>
              </div>
            </div>

            <div className="sidebar-body" ref={sidebarRef}>
              {isLoading ? (
                <ul className="list-unstyled chat-contact-list mb-0 py-2">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <li
                      key={idx}
                      className="chat-contact-list-item d-flex align-items-center px-3 py-2"
                    >
                      <Skeleton
                        variant="circular"
                        width={40}
                        height={40}
                        animation="wave"
                      />
                      <div className="chat-contact-info flex-grow-1 ms-3">
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={20}
                          animation="wave"
                        />
                        <Skeleton
                          variant="text"
                          width="40%"
                          height={16}
                          animation="wave"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="list-unstyled chat-contact-list mb-0 py-2">
                  {isListPassword.map((pwd) => (
                    <li
                      key={pwd._id}
                      className={`chat-contact-list-item d-flex align-items-center px-3 py-2 cursor-pointer ${
                        selected?._id === pwd._id ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelected(pwd);
                        const sidebar =
                          document.getElementById("app-chat-contacts");
                        const overlay =
                          document.getElementById("sidebar-overlay");
                        if (window.innerWidth < 992) {
                          sidebar?.classList.remove("show");
                          overlay?.remove();
                        }
                      }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor:
                            selected?._id === pwd._id ? "#fff" : "#6c757d",
                          color: selected?._id === pwd._id ? "#0d6efd" : "#fff",
                          fontWeight: "bold",
                        }}
                      >
                        {pwd.title.charAt(0).toUpperCase()}
                      </div>
                      <div className="chat-contact-info flex-grow-1 ms-3">
                        <h6 className="chat-contact-name text-truncate m-0 fw-normal">
                          {pwd.title}
                        </h6>
                        <small className="chat-contact-status text-truncate">
                          {pwd.userName}
                        </small>
                        {/* <small className="chat-contact-status text-truncate">
                          {pwd?.notes
                            ? pwd.notes.length > 20
                              ? pwd.notes.slice(0, 20) + "..."
                              : pwd.notes
                            : ""}
                        </small> */}
                      </div>
                    </li>
                  ))}
                  {isLoadingMore && (
                    <div className="text-center py-2">
                      <CircularProgress size={25} />
                    </div>
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* ---------------- RIGHT CONTENT (CHAT HISTORY) ---------------- */}
          <div className="col app-chat-history d-block">
            <div className="chat-history-wrapper">
              {!selected ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-muted mt-5 text-center">
                  {/* 👇 Menu button (mobile only) */}
                  <i
                    className="ng ng-ng-add-icon cursor-pointer d-lg-none d-block mb-3 fs-2"
                    onClick={() => {
                      const sidebar =
                        document.getElementById("app-chat-contacts");
                      if (sidebar) {
                        sidebar.classList.add("show");
                        // Add overlay for mobile
                        const overlay = document.createElement("div");
                        overlay.className = "sidebar-overlay";
                        overlay.id = "sidebar-overlay";
                        overlay.onclick = () => {
                          sidebar.classList.remove("show");
                          overlay.remove();
                        };
                        document.body.appendChild(overlay);
                      }
                    }}
                  ></i>

                  {/* 🔒 Icon and message */}
                  <LockAltIcon iClass={"fs-1 mb-2"}/>
                  <p>Select a password entry from the Add</p>
                </div>
              ) : (
                <>
                  <div className="chat-history-header border-bottom d-flex align-items-center pb-3">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                      <i
                        className={`bx bx-menu fs-14 ${
                          isHovered ? "mobile-toggle-menu" : "head-icon"
                        } d-lg-none me-2 text-dark align-middle cursor-pointer`}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onClick={() => {
                          const sidebar =
                            document.getElementById("app-chat-contacts");
                          if (sidebar) {
                            sidebar.classList.add("show");
                            const overlay = document.createElement("div");
                            overlay.className = "sidebar-overlay";
                            overlay.id = "sidebar-overlay";
                            overlay.onclick = () => {
                              sidebar.classList.remove("show");
                              overlay.remove();
                            };
                            document.body.appendChild(overlay);
                          }
                        }}
                      ></i>

                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-secondary text-white"
                        style={{
                          width: "40px",
                          height: "40px",
                          fontWeight: "bold",
                        }}
                      >
                        {selected.title.charAt(0).toUpperCase()}
                      </div>
                      <h6 className="mb-0 fw-semibold text-truncate">
                        {selected.title}
                      </h6>
                    </div>
                    <div className="order-actions ms-auto d-flex gap-2">
                      <button
                        className="btn btn-label-primary shadow-none"
                        onClick={() => setShowEditModal(true)}
                      >
                        <EditIcon/>
                      </button>
                      <button
                        className="btn  btn-label-danger shadow-none "
                        onClick={() => openDeleteModal(selected)}
                      >
                        <DeleteIcon/>
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="chat-history-body flex-grow-1 overflow-auto p-3">
                    <div
                      className="mb-3 p-3 border rounded shadow-sm d-flex justify-content-between align-items-start hover-copy"
                      onClick={() => {
                        handleCopy(selected.userName, "username");
                        setShowPassword(false);
                      }}
                    >
                      <strong>
                        {" "}
                        <UserIcon iClass={"me-1"}/>Username
                      </strong>
                      <div
                        className="ms-4 cursor-pointer text-primary "
                        style={{ fontSize: "1rem" }}
                      >
                        {selected.userName}
                        <CopyIcon iClass={"ms-1 copy-icon"}/>
                      </div>
                    </div>

                    {/* Password */}
                    <div
                      className="mb-3 p-3 border rounded shadow-sm d-flex justify-content-between align-items-start hover-copy"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Copy only if password is already fetched
                        if (selected?.password && passwordUnlocked) {
                          handleCopy(selected.password, "password");
                        }
                      }}
                      onMouseLeave={() => setShowPassword(false)}
                    >
                      <strong>
                        {" "}
                        <LockAltIcon/> Password
                      </strong>
                      <div
                        className="ms-4 cursor-pointer text-primary text-end"
                        style={{ fontSize: "1rem", minWidth: "120px" }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!passwordUnlocked || !selected.password) {
                            const fetched = await handleViewPassword(selected);
                            if (fetched) {
                              setSelected((prev) => ({
                                ...prev,
                                password: fetched.password,
                              }));
                            }
                            setPasswordUnlocked(true);
                            setShowPassword(true);
                          } else {
                            setShowPassword((prev) => !prev);
                          }
                        }}
                        onMouseEnter={() => {
                          if (passwordUnlocked) setShowPassword(true);
                        }}
                        onMouseLeave={() => setShowPassword(false)}
                      >
                        {isPasswordLoading ? (
                          <Skeleton width={100} height={20} />
                        ) : showPassword ? (
                          selected.password
                        ) : (
                          Array.from({ length: 6 }).map((_, i) => (
                            <span key={i} style={{ marginRight: "2px" }}>
                              •
                            </span>
                          ))
                        )}

                        {!isPasswordLoading && passwordUnlocked && (
                          <i
                            className="bx bx-copy ms-1 copy-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(selected.password, "password");
                            }}
                          ></i>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    <div
                      className=" mb-3 p-2 border rounded shadow-sm"
                      onClick={() => setShowPassword(false)}
                    >
                      <strong>
                        <NoteIcon iClass={"me-1"}/>Notes
                      </strong>
                      <div
                        className="ms-4 mt-2 mb-2 text-primary"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        {selected?.notes?.slice(0, 500)}
                      </div>
                    </div>

                    <div className="mb-3  d-flex justify-content-end">
                      <p className="small mb-0 ">
                        <b className="tm_primary_color"> Created At:</b>{" "}
                        {
                          DateConverter.ISOToDisplayFormat(
                            DateConverter.convertToTimeZone(
                              selected.createdAt,
                              "Asia/Kolkata"
                            )
                          ).date
                        }{" "}
                        {
                          DateConverter.ISOToDisplayFormat(
                            DateConverter.convertToTimeZone(
                              selected.createdAt,
                              isUser?.timezone
                            )
                          ).time
                        }
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ---------------- MODAL ---------------- */}
          {showModal && (
            <AddNewPassword
              onClose={handleClose}
              onAdd={handleAddPassword}
              isBtnLoading={false}
            />
          )}

          {showEditModal && selected && (
            <EditPassword
              onClose={() => setShowEditModal(false)}
              editData={selected}
              onUpdatePassword={onUpdatePassword}
              onUpdateDetails={onUpdateDetails}
            />
          )}

          {isDeleteModalOpen && (
            <DeleteModal
              onClose={() => setDeleteModalOpen(false)}
              onConfirm={handleDelete}
              isLable={" Password " + isDeleteData?.title}
              isBtnLoading={isBtnLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordManager;
